import React, { useEffect, useState } from "react";
import { Row, Col, Container, Form, FloatingLabel, Button, Spinner, Dropdown, DropdownButton } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { fetchGroup, fetchGroupMeetings, fetchGroupParticipants, postGroupMeetings, postNote } from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import ToastMsg from "../ui/ToastMsg";
import GroupMeetingIndivNote from "./GroupMeetingIndivNote";
import DatePicker from "../ui/DatePicker";
import useApp from "../../utils/useApp";
import SaveChangesModal from "../ui/SaveChangesModal";
import { unsavedChanges } from "../../App";

const GroupMeeting = () => {
  const { id, meetingId } = useParams();
  const { showMenu }: any = useApp();
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [participants, setParticipants] = useState<any[]>([]);
  const [attendanceStats, setAttendanceStats] = useState<any>({});
  const [meetings, setMeetings] = useState<any[]>([]);
  const [meetingsList, setMeetingsList] = useState<any[]>([]);
  const [pendingPath, setPendingPath] = useState<string>("/groups");
  const [groupNote, setGroupNote] = useState<string>("");
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [enableSave, setEnableSave] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [toastDisplayed, setToastDisplayed] = useState(false);
  const hasPresentParticipants = participants.some((p) => p.is_present);
  const isReadOnly = !!meetingId;

  let navigate = useNavigate();

  const goBack = () => {
    if (isReadOnly) {
      unsavedChanges.value = false;
      setGroupNote("");
      setMeetingDate("");
      getParticipants();
      navigate(`/groups/group/${id}/meeting`);
    } else if (!isReadOnly && (enableSave || unsavedChanges.value === true)) {
      setPendingPath(`/groups`);
      setShowModal(true);
    } else {
      navigate(`/groups`);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getMeetings();

    fetchGroup(id)
      .then((res: any) => {
        const currentGroup = res[0].data;
        setName(currentGroup.name);
        setDescription(currentGroup.description);

        if (!isReadOnly) {
          getParticipants();
        } else {
          return;
        }
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!isReadOnly || !meetingId || meetings.length === 0) return;
    const meeting = meetings.find((m) => String(m.id) === String(meetingId));

    if (!meeting) {
      setIsLoading(false);
      return;
    }

    setGroupNote(meeting.group_note);
    setMeetingDate(meeting.meeting_at.split(" ")[0]);

    setParticipants(
      meeting.participants.map((p: any) => ({
        user_id: p.participant_id,
        type: p.participant_type,
        firstname: p.firstname,
        lastname: p.lastname ?? "",
        note: p.participant_note,
        is_present: p.attended,
      })),
    );

    setIsLoading(false);
  }, [isReadOnly, meetingId, meetings]);

  const getParticipants = () => {
    fetchGroupParticipants(id)
      .then((res: any) => {
        const currentParts = res[0].data;

        const parts = currentParts.map((c: any) => ({
          user_id: c?.participant_id,
          type: c?.participant_type,
          firstname: c?.firstname,
          lastname: c?.lastname ?? "",
          case_id: c?.caseid,
          note: "",
          is_present: true,
        }));

        setParticipants(parts);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const createMeeting = () => {
    setErrMsg("");
    setIsLoading(true);

    const newMeeting = {
      group_note: groupNote,
      meeting_at: meetingDate,
      participants: participants.map((p) => ({
        participant_id: p.user_id,
        participant_type: p.type,
        participant_note: p.note,
        attended: p.is_present,
      })),
    };

    postGroupMeetings(newMeeting, id)
      .then(() => {
        const filteredParticipants = participants.filter((p) => p.is_present || p.note.trim().length > 0);

        const notePromises = filteredParticipants.map((p) => {
          const newNote = {
            notetype: "group",
            subject: {
              id: p.user_id,
              type: p.type,
            },
            fields: {
              group_name: name,
              group_note: groupNote,
              individual_note: p.note,
              date: meetingDate,
            },
          };

          return postNote(newNote, p.case_id).catch((err) => {
            console.log(err);
            setErrMsg("Failed to create notes");
            return null;
          });
        });

        Promise.all(notePromises).then(() => {
          if (!toastDisplayed) {
            setShowToast(true);
            setToastText("Group Meeting Created");
            setToastBody(name);
            setToastDisplayed(true);
          }

          setTimeout(() => {
            setIsLoading(false);
            unsavedChanges.value = false;
            navigate(`/groups`);
          }, 1500);
        });
      })
      .catch((err: any) => {
        console.log(err);
        setErrMsg("Failed to create group meeting");
        setIsLoading(false);
      });
  };

  const buildAttendanceStats = (meetings: any[]) => {
    const stats: Record<number, { attended: number; total: number }> = {};

    meetings.forEach((meeting) => {
      meeting.participants.forEach((p: any) => {
        if (!stats[p.participant_id]) {
          stats[p.participant_id] = { attended: 0, total: 0 };
        }

        stats[p.participant_id].total += 1;

        if (p.attended === 1) {
          stats[p.participant_id].attended += 1;
        }
      });
    });

    return stats;
  };

  const getMeetings = () => {
    if (!isReadOnly) setIsLoading(false);

    fetchGroupMeetings(id)
      .then((res: any) => {
        const meetings = res[0].data;
        setMeetings(meetings);

        setMeetingsList(
          meetings.map((m: any) => ({
            key: m.id,
            name: new Date(m.meeting_at).toLocaleDateString(),
          })),
        );

        setAttendanceStats(buildAttendanceStats(meetings));
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const selectPreviousMeeting = (meetingId: string) => {
    if (unsavedChanges.value === true) {
      setPendingPath(`/groups/group/${id}/meeting/${meetingId}`);
      setShowModal(true);
      return;
    }

    navigate(`/groups/group/${id}/meeting/${meetingId}`);
  };

  const updateGroupNote = (e: any) => {
    setGroupNote(e.target.value);
    unsavedChanges.value = true;

    if (meetingDate) {
      setEnableSave(true);
    }
  };

  useEffect(() => {
    if (meetingDate) {
      setEnableSave(true);
    } else {
      setEnableSave(false);
    }
  }, [meetingDate]);

  useEffect(() => {
    if (!isReadOnly && enableSave) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [isReadOnly, enableSave]);

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle title={name} backButtonText={`Back to ${isReadOnly ? "Meeting" : "Groups"}`} onBackClick={goBack} />
          <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
            <h6 className="text-muted mb-0">{description}</h6>
            {meetings.length > 0 && (
              <DropdownButton
                id="previous-meetings-dropdown"
                size="sm"
                align="end"
                variant="outline-secondary"
                className="flex-shrink-0"
                title="Previous Meetings"
              >
                {meetingsList.map((m: any) => (
                  <Dropdown.Item
                    key={m.key}
                    className={String(m.key) === String(meetingId) ? "app-tertiary-color-inverse" : ""}
                    onClick={() => selectPreviousMeeting(m.key)}
                  >
                    {m.name}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            )}
          </div>
          <Row>
            <Col xs={12} lg={6}>
              <h6 className="app-header-text mt-2">Participants</h6>
              {participants.map((p: any, index: number) => (
                <GroupMeetingIndivNote
                  key={index}
                  participant={p}
                  index={index}
                  participants={participants}
                  setParticipants={setParticipants}
                  attendanceStats={attendanceStats[p.user_id]}
                  setEnableSave={setEnableSave}
                  meetingDate={meetingDate}
                  readOnly={isReadOnly}
                />
              ))}
            </Col>
            <Col xs={12} lg={6}>
              <h6 className="app-header-text mt-2">Meeting</h6>
              <FloatingLabel controlId="floatingInput" label="Group Note" className="sm mb-3 mt-1">
                <Form.Control
                  type="text"
                  placeholder="Group Note"
                  value={groupNote}
                  onChange={(e) => updateGroupNote(e)}
                  disabled={isReadOnly}
                />
              </FloatingLabel>
              <Col xs={6} md={4}>
                <DatePicker
                  label="Date"
                  currentValue={meetingDate}
                  setCurrentValue={setMeetingDate}
                  required={true}
                  hasMaxDate={true}
                  disabled={isReadOnly}
                />
              </Col>
            </Col>
          </Row>
          {!isReadOnly && (
            <Row className="mt-3 pb-5">
              <Col xs={4}>
                <Button
                  className="app-secondary-color me-3 button"
                  onClick={(e) => createMeeting()}
                  disabled={!enableSave || !hasPresentParticipants}
                >
                  {"Save Meeting"}
                </Button>
              </Col>
            </Row>
          )}
          <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path={pendingPath} />
        </Container>
      )}
    </>
  );
};

export default GroupMeeting;
